import { supabaseClient } from "@/lib/supabase";
import type { Json } from "@/types/database/database";
import type { ScreenerCriteria } from "@patient-watch/forms";
import type { ScreenerT } from "@patient-watch/types";
import { toast } from "@patient-watch/ui/sonner";
import { queryOptions, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

export const screenerBuilderKeys = {
    all: ["screeners", "builder"] as const,
    byForm: (formId: string) => ["screeners", "builder", { form_id: formId }] as const,
};

export type ScreenerUpdateT = Partial<
    Pick<
        ScreenerT,
        "is_active" | "label_id" | "eligible_title" | "eligible_body" | "ineligible_title" | "ineligible_body"
    >
> & { criteria?: ScreenerCriteria };

/** The form's screener, active or not. Admins only. */
export async function selectFormScreener(formId: string): Promise<ScreenerT | null> {
    const { data, error } = await supabaseClient.from("screeners").select().eq("form_id", formId).maybeSingle();
    if (error) throw error;
    return data;
}

export function useFormScreener(formId: string, { enabled = true }: { enabled?: boolean } = {}) {
    return useQuery(
        queryOptions({
            queryKey: screenerBuilderKeys.byForm(formId),
            queryFn: () => selectFormScreener(formId),
            enabled,
        })
    );
}

export interface ScreenerOptionT {
    screener_id: string;
    form_name: string;
    is_active: boolean;
}

/** Every screener by form name, for picking one on a template. */
export async function selectScreenerOptions(): Promise<ScreenerOptionT[]> {
    const { data, error } = await supabaseClient
        .from("screeners")
        .select("screener_id, is_active, forms(form_name)")
        .order("created_at", { ascending: false });
    if (error) throw error;
    return (data ?? []).map((row) => ({
        screener_id: row.screener_id,
        is_active: row.is_active,
        form_name: row.forms?.form_name ?? "Untitled form",
    }));
}

export function useScreenerOptions({ enabled = true }: { enabled?: boolean } = {}) {
    return useQuery({ queryKey: screenerBuilderKeys.all, queryFn: selectScreenerOptions, enabled });
}

export function useCreateScreener(formId: string) {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async () => {
            const { data, error } = await supabaseClient
                .from("screeners")
                .insert({ form_id: formId })
                .select()
                .single();
            if (error) throw error;
            return data;
        },
        onSuccess: (screener) => {
            queryClient.setQueryData(screenerBuilderKeys.byForm(formId), screener);
            void queryClient.invalidateQueries({ queryKey: screenerBuilderKeys.all });
        },
        onError: () => toast.error("Could not set up the screener"),
    });
}

export function useUpdateScreener(formId: string) {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async ({ screenerId, update }: { screenerId: string; update: ScreenerUpdateT }) => {
            const { criteria, ...rest } = update;
            const { error } = await supabaseClient
                .from("screeners")
                .update({
                    ...rest,
                    ...(criteria ? { criteria: criteria as unknown as Json } : {}),
                    updated_at: new Date().toISOString(),
                })
                .eq("screener_id", screenerId);
            if (error) throw error;
        },
        onSuccess: () => {
            void queryClient.invalidateQueries({ queryKey: screenerBuilderKeys.byForm(formId) });
            void queryClient.invalidateQueries({ queryKey: screenerBuilderKeys.all });
        },
    });
}

export function useDeleteScreener(formId: string) {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async (screenerId: string) => {
            const { error } = await supabaseClient.from("screeners").delete().eq("screener_id", screenerId);
            if (error) throw error;
        },
        onSuccess: () => {
            queryClient.setQueryData(screenerBuilderKeys.byForm(formId), null);
            void queryClient.invalidateQueries({ queryKey: screenerBuilderKeys.all });
        },
        onError: () => toast.error("Could not remove the screener"),
    });
}
